import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import { hotTitles, searchContent } from "@/utils/content";
import { newsList } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const suggestions = ["悬疑", "科幻", "年代", "治愈", "犯罪", "古装"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";
  const [input, setInput] = useState(queryParam);

  useDocumentMeta(
    queryParam ? `搜索「${queryParam}」- 午夜影院` : "站内搜索 - 午夜影院",
    "在午夜影院搜索电影、电视剧、资讯与专题内容。"
  );

  useEffect(() => {
    setInput(queryParam);
  }, [queryParam]);

  const result = useMemo(() => searchContent(input), [input]);
  const movies = result.titles.filter((item) => item.type === "movie");
  const series = result.titles.filter((item) => item.type === "tv");
  const hot = hotTitles(4);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (input.trim()) {
      setSearchParams({ q: input.trim() });
    } else {
      setSearchParams({});
    }
  };

  const applySuggestion = (word: string) => {
    setInput(word);
    setSearchParams({ q: word });
  };

  return (
    <div className="w-full bg-background-50">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-9 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="transition-colors duration-300 hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-600">站内搜索</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-[32px]">
            搜索影视与资讯
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            输入片名、类型、地区或资讯关键词。当前为前端演示搜索，结果来自站内示例内容。
          </p>

          <form onSubmit={handleSubmit} className="mt-5 flex w-full max-w-2xl items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="试试「悬疑」「科幻」「雾港」"
                aria-label="搜索关键词"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-primary-950 transition-colors duration-300 hover:bg-accent-400"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-400">热门关键词</span>
            {suggestions.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => applySuggestion(word)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors duration-200 hover:border-accent-400 hover:text-accent-700"
              >
                {word}
              </button>
            ))}
          </div>

          {input.trim() ? (
            <p className="mt-4 text-xs text-foreground-500">
              关键词「<span className="text-foreground-900">{input.trim()}</span>」共找到{" "}
              <span className="font-label text-accent-700">{result.total}</span> 条内容
            </p>
          ) : null}
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        {!input.trim() ? (
          <div>
            <SectionHeading
              kicker="不知道看什么"
              title="先看看大家都在搜什么"
              description="从热门作品与最新资讯开始，或者直接输入关键词。"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {hot.map((item) => (
                <MovieCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        ) : result.total === 0 ? (
          <div className="space-y-10">
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与「${input.trim()}」相关的内容`}
              description="可以尝试更换关键词，比如片名的一部分、类型（悬疑、科幻）或地区（美国、日本）。"
              actionLabel="清空搜索"
              onAction={() => {
                setInput("");
                setSearchParams({});
              }}
            />
            <div>
              <SectionHeading kicker="为你推荐" title="热门影视作品" />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {hot.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>
            </div>
            <div>
              <SectionHeading kicker="延伸阅读" title="热门资讯" actionLabel="全部资讯" actionTo="/news" />
              <ul className="divide-y divide-background-200 border-y border-background-200">
                {newsList.slice(0, 4).map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="group flex items-center justify-between gap-4 py-3.5"
                    >
                      <span className="min-w-0 line-clamp-1 text-sm text-foreground-800 transition-colors duration-300 group-hover:text-accent-700">
                        {item.title}
                      </span>
                      <span className="shrink-0 font-label text-[11px] text-foreground-400">
                        {item.date}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-12">
            {movies.length > 0 ? (
              <section>
                <SectionHeading
                  kicker={`电影 · ${movies.length} 个结果`}
                  title="电影"
                  actionLabel="电影分类"
                  actionTo="/category/movie"
                />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                  {movies.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            ) : null}

            {series.length > 0 ? (
              <section>
                <SectionHeading
                  kicker={`电视剧 · ${series.length} 个结果`}
                  title="电视剧"
                  actionLabel="电视剧分类"
                  actionTo="/category/tv"
                />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                  {series.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            ) : null}

            {result.news.length > 0 ? (
              <section>
                <SectionHeading
                  kicker={`资讯 · ${result.news.length} 个结果`}
                  title="资讯"
                  actionLabel="全部资讯"
                  actionTo="/news"
                />
                <ul className="divide-y divide-background-200 border-y border-background-200">
                  {result.news.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/detail/${item.id}`}
                        className="group flex items-start gap-4 py-4"
                      >
                        <span className="hidden w-16 shrink-0 pt-0.5 font-label text-[11px] text-foreground-400 sm:block">
                          {item.date.slice(5)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-[10px] text-accent-900">
                              {item.category}
                            </span>
                            <span className="line-clamp-1 text-sm text-foreground-900 transition-colors duration-300 group-hover:text-accent-700">
                              {item.title}
                            </span>
                          </span>
                          <span className="mt-1 line-clamp-1 block text-xs text-foreground-500">
                            {item.excerpt}
                          </span>
                        </span>
                        <i className="ri-arrow-right-up-line mt-1 shrink-0 text-foreground-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {result.specials.length > 0 ? (
              <section>
                <SectionHeading kicker={`专题 · ${result.specials.length} 个结果`} title="专题" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
                  {result.specials.map((item) => (
                    <Link
                      key={item.id}
                      to={`/special/${item.id}`}
                      className="group rounded-lg border border-background-200 bg-background-100 p-4 transition-colors duration-300 hover:border-accent-300"
                    >
                      <p className="font-label text-[10px] uppercase tracking-[0.18em] text-secondary-700">
                        {item.month}
                      </p>
                      <h3 className="mt-1.5 font-heading text-base font-semibold text-foreground-950 group-hover:text-accent-700">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                        {item.intro}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}

          </div>
        )}
      </div>
    </div>
  );
}